<script setup>
import { ref, computed } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useAcesso } from 'dashboard/composables/useAcesso';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Auth from '../../../../api/auth';
import wootConstants from 'dashboard/constants/globals';
import SeletorDePapel from './SeletorDePapel.vue';
import OndeAtua from './OndeAtua.vue';
import ExcecoesDaPessoa from './ExcecoesDaPessoa.vue';
import { valorDoPapel, corpoDoPapel, fraseDaFalha } from './acessoDaPessoa';

const props = defineProps({
  id: {
    type: Number,
    required: true,
  },
  name: {
    type: String,
    required: true,
  },
  email: {
    type: String,
    default: '',
  },
  type: {
    type: String,
    default: '',
  },
  availability: {
    type: String,
    default: '',
  },
  provider: {
    type: String,
    default: '',
  },
  customRoleId: {
    type: Number,
    default: null,
  },
  // ACESSO-01 (L9b): o papel da pessoa (`agent.papel`), o modelo da Powerbizz ou o papel da conta.
  papel: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['close']);

const { AVAILABILITY_STATUS_KEYS } = wootConstants;

const store = useStore();
const { t } = useI18n();

const agentName = ref(props.name);
const agentAvailability = ref(props.availability);
const selectedRoleId = ref(props.type);
const agentCredentials = ref({ email: props.email });
// `''` é "nenhum papel personalizado" — é o valor que o <select> consegue carregar numa <option>
// vazia, e o que o backend trata como desatribuir.
const selectedCustomRoleId = ref(props.customRoleId ?? '');

const rules = {
  agentName: { required, minLength: minLength(1) },
  selectedRoleId: { required },
  agentAvailability: { required },
};

const v$ = useVuelidate(rules, {
  agentName,
  selectedRoleId,
  agentAvailability,
});

const pageTitle = computed(
  () => `${t('AGENT_MGMT.EDIT.TITLE')} - ${props.name}`
);

const uiFlags = useMapGetter('agents/getUIFlags');

// Só administrador atribui papel personalizado (o backend recusa o resto), então o campo não
// aparece para quem não pode — campo que existe e sempre dá erro é pior que campo ausente.
const { isAdmin } = useAdmin();
const customRoles = useMapGetter('customRoles/getCustomRoles');
// ACESSO-01 (L8b): com a chave ACESSO_GRANULAR ligada, entregar papel personalizado é do `roles:manage`
// (`agents_controller`); desligada, do administrador, como acima.
const { decide, ligado, pode } = useAcesso();
const entregaPapel = computed(() =>
  decide('roles:manage', () => isAdmin.value)
);

// ACESSO-01 (L9b): com a chave ligada, o papel base e o papel personalizado dão lugar a um select só, o "Papel" (quadro 6
// do mock v3), de quem tem o `people:manage`; sem ele, o papel aparece em texto. Logo abaixo vêm o "Onde atua" e as
// exceções da pessoa, também do `people:manage`, que gravam sozinhos. Desligada, o modal fica como hoje.
const podePessoas = computed(() => pode('people:manage'));
const papelDeAntes = props.papel ? valorDoPapel(props.papel) : '';
const papelEscolhido = ref(papelDeAntes);
const rotuloDoPapel = `papel-da-pessoa-${props.id}`;

// As exceções gravam na hora, sem o Salvar: a lista de agentes fica com a conta das que estão em vigor e das vencidas.
const contarExcecoesNaLista = (emVigor, vencidas) =>
  store.dispatch('agents/atualizarExcecoesEmVigor', {
    id: props.id,
    emVigor,
    vencidas,
  });

const roles = computed(() => {
  return [
    {
      id: 'administrator',
      name: 'administrator',
      label: t('AGENT_MGMT.AGENT_TYPES.ADMINISTRATOR'),
    },
    {
      id: 'moderator',
      name: 'moderator',
      label: t('AGENT_MGMT.AGENT_TYPES.MODERATOR'),
    },
    {
      id: 'agent',
      name: 'agent',
      label: t('AGENT_MGMT.AGENT_TYPES.AGENT'),
    },
  ];
});

const selectedRole = computed(() =>
  roles.value.find(
    role =>
      role.id === selectedRoleId.value || role.name === selectedRoleId.value
  )
);

const statusList = computed(() => {
  return [
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.ONLINE'),
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.BUSY'),
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.OFFLINE'),
  ];
});

const availabilityStatuses = computed(() =>
  statusList.value.map((statusLabel, index) => ({
    label: statusLabel,
    value: AVAILABILITY_STATUS_KEYS[index],
    disabled: props.availability === AVAILABILITY_STATUS_KEYS[index],
  }))
);

const editAgent = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  try {
    const payload = {
      id: props.id,
      name: agentName.value,
      availability: agentAvailability.value,
    };

    if (ligado.value) {
      // ACESSO-01 (L9b): o papel vai só quando muda. Mandar o mesmo gravaria na pessoa o modelo que ela tem pelo papel
      // base, e quem só troca o nome teria de poder dá-lo (`atribuicao` do `AgentsController`).
      if (papelEscolhido.value !== papelDeAntes) {
        Object.assign(payload, corpoDoPapel(papelEscolhido.value));
      }
    } else {
      payload.role = selectedRole.value.name;
      // Vai no payload só quando quem edita pode mexer nisso; mandar a chave sempre faria um
      // moderador levar 401 ao salvar uma simples troca de nome.
      if (entregaPapel.value) {
        payload.custom_role_id = selectedCustomRoleId.value;
      }
    }

    await store.dispatch('agents/update', payload);
    useAlert(t('AGENT_MGMT.EDIT.API.SUCCESS_MESSAGE'));
    emit('close');
  } catch (error) {
    // ACESSO-01 (L9b): a recusa do acesso diz a frase do servidor, como "Você não pode dar o que não tem".
    useAlert(fraseDaFalha(error, t('AGENT_MGMT.EDIT.API.ERROR_MESSAGE')));
  }
};

const resetPassword = async () => {
  try {
    await Auth.resetPassword(agentCredentials.value);
    useAlert(t('AGENT_MGMT.EDIT.PASSWORD_RESET.ADMIN_SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('AGENT_MGMT.EDIT.PASSWORD_RESET.ERROR_MESSAGE'));
  }
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header :header-title="pageTitle" />
    <form class="w-full" @submit.prevent="editAgent">
      <div class="w-full">
        <label :class="{ error: v$.agentName.$error }">
          {{ $t('AGENT_MGMT.EDIT.FORM.NAME.LABEL') }}
          <input
            v-model="agentName"
            type="text"
            :placeholder="$t('AGENT_MGMT.EDIT.FORM.NAME.PLACEHOLDER')"
            @input="v$.agentName.$touch"
          />
        </label>
      </div>

      <template v-if="!ligado">
        <div class="w-full">
          <label :class="{ error: v$.selectedRoleId.$error }">
            {{ $t('AGENT_MGMT.EDIT.FORM.AGENT_TYPE.LABEL') }}
            <select v-model="selectedRoleId" @change="v$.selectedRoleId.$touch">
              <option v-for="role in roles" :key="role.id" :value="role.id">
                {{ role.label }}
              </option>
            </select>
            <span v-if="v$.selectedRoleId.$error" class="message">
              {{ $t('AGENT_MGMT.EDIT.FORM.AGENT_TYPE.ERROR') }}
            </span>
          </label>
        </div>

        <div v-if="entregaPapel" class="w-full">
          <label>
            {{ $t('AGENT_MGMT.EDIT.FORM.CUSTOM_ROLE.LABEL') }}
            <select v-model="selectedCustomRoleId">
              <option value="">
                {{ $t('AGENT_MGMT.EDIT.FORM.CUSTOM_ROLE.NONE') }}
              </option>
              <option
                v-for="role in customRoles"
                :key="role.id"
                :value="role.id"
              >
                {{ role.name }}
              </option>
            </select>
            <p class="text-body-main text-n-slate-11">
              {{ $t('AGENT_MGMT.EDIT.FORM.CUSTOM_ROLE.HELP') }}
            </p>
          </label>
        </div>
      </template>

      <div class="w-full">
        <label :class="{ error: v$.agentAvailability.$error }">
          {{ $t('PROFILE_SETTINGS.FORM.AVAILABILITY.LABEL') }}
          <select
            v-model="agentAvailability"
            @change="v$.agentAvailability.$touch"
          >
            <option
              v-for="status in availabilityStatuses"
              :key="status.value"
              :value="status.value"
            >
              {{ status.label }}
            </option>
          </select>
          <span v-if="v$.agentAvailability.$error" class="message">
            {{ $t('AGENT_MGMT.EDIT.FORM.AGENT_AVAILABILITY.ERROR') }}
          </span>
        </label>
      </div>

      <div v-if="ligado" class="flex flex-col w-full gap-6 pb-4">
        <div class="flex flex-col" data-papel>
          <span
            :id="rotuloDoPapel"
            class="text-sm font-medium leading-7 text-n-slate-12"
          >
            {{ $t('AGENT_MGMT.ACESSO.PAPEL.LABEL') }}
          </span>
          <SeletorDePapel
            v-if="podePessoas"
            v-model="papelEscolhido"
            :atual="papel"
            :aria-labelledby="rotuloDoPapel"
          />
          <p v-else class="mb-0 text-sm text-n-slate-12" data-papel-em-texto>
            {{ papel?.nome }}
          </p>
        </div>
        <OndeAtua v-if="podePessoas" :pessoa-id="id" />
        <ExcecoesDaPessoa
          v-if="podePessoas"
          :pessoa-id="id"
          @em-vigor="contarExcecoesNaLista"
        />
      </div>

      <div class="flex flex-row justify-start w-full gap-2 px-0 py-2">
        <div class="w-[50%] ltr:text-left rtl:text-right">
          <Button
            v-if="provider !== 'saml'"
            ghost
            type="button"
            icon="i-lucide-lock-keyhole"
            class="!px-2"
            :label="$t('AGENT_MGMT.EDIT.PASSWORD_RESET.ADMIN_RESET_BUTTON')"
            @click.prevent="resetPassword"
          />
        </div>
        <div class="w-[50%] flex justify-end items-center gap-2">
          <Button
            faded
            slate
            type="reset"
            :label="$t('AGENT_MGMT.EDIT.CANCEL_BUTTON_TEXT')"
            @click.prevent="emit('close')"
          />
          <Button
            type="submit"
            :label="$t('AGENT_MGMT.EDIT.FORM.SUBMIT')"
            :disabled="v$.$invalid || uiFlags.isUpdating"
            :is-loading="uiFlags.isUpdating"
          />
        </div>
      </div>
    </form>
  </div>
</template>
